<script setup lang="ts">
/* ---------------------------------------------------------------------------------------------- */

import type { QueryMode } from '@/utils/query-routing';
import { Icon } from '@iconify/vue';
import { useSpeechRecognition } from '@vueuse/core';
import { nextTick, ref, useTemplateRef, watch } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import QueryModeSubmit from '@/components/query-routing/query-mode-submit.vue';
import { UiTooltip } from '@/components/ui';
import { forcedModeFromEvent, queryModeHint } from '@/utils/query-routing';

/* ---------------------------------------------------------------------------------------------- */

const props = withDefaults(defineProps<{
   isLoading?: boolean;
   bordered?: boolean;
   clearInputOnSend?: boolean;
   /**
    * ENG-436: opt in to search-vs-chat routing. When set, modifier + Enter
    * emits a forced mode alongside the message so the caller can hand it to
    * the other interface.
    */
   routable?: boolean;
   placeholder?: string;
}>(), {
   clearInputOnSend: true,
   routable: false,
   placeholder: 'Type message',
});

const emit = defineEmits<{
   /** `force` is only ever set when `routable` is on and a modifier was held. */
   sendMessage: [message: string, force: QueryMode | null];
}>();

const model = defineModel<string>({ required: true });

/* ---------------------------------------------------------------------------------------------- */

const inputRef = useTemplateRef<HTMLInputElement>('inputRef');

// Track the base text (what was already in the input before speech started)
const baseText = ref('');
// Track the current speech session text
const currentSpeechText = ref('');

/* ---------------------------------------------------------------------------------------------- */

const {
   isSupported,
   isListening,
   isFinal,
   result,
   start,
   stop,
} = useSpeechRecognition();

/**
 * The recognizer's `result` is a readonly ref that useSpeechRecognition does
 * NOT clear between sessions, and its `onstart` resets `isFinal` (which this
 * watcher observes). Snapshot the leftover transcript at (re)start so that
 * reset can't replay the previous session's text into `model`; ignore it until
 * a genuinely new result arrives.
 */
let staleResult = '';

/**
 * Watch for speech recognition results
 */
watch([result, isFinal], () => {
   if (!result.value || result.value === staleResult)
      return;

   // Update the current speech text
   currentSpeechText.value = result.value;

   // Combine base text with current speech
   const combinedText = baseText.value + (baseText.value ? ' ' : '') + currentSpeechText.value;
   model.value = combinedText;

   // If the result is final, commit it to a base text
   if (isFinal.value) {
      baseText.value = combinedText;
      currentSpeechText.value = '';
   }
});

/**
 * Autofocus and scroll to end when a text is added via speech
 */
watch(model, async () => {
   if (inputRef.value) {
      await nextTick();
      inputRef.value.focus();
      inputRef.value.scrollLeft = inputRef.value.scrollWidth;
   }
});

/* ---------------------------------------------------------------------------------------------- */

/**
 * Initializes and starts the listening process.
 */
function startListening() {
   model.value = '';
   baseText.value = '';
   currentSpeechText.value = '';
   // Ignore any transcript left over from a previous session (see staleResult).
   staleResult = result.value;

   start();
}

/**
 * Stops the current listening or speech recognition process and resets associated text values.
 */
function stopListening() {
   currentSpeechText.value = '';
   baseText.value = '';

   stop();
}

/**
 * Handles the input change event by updating the value of the baseText element
 */
function handleInputChange() {
   baseText.value = model.value;
}

function handleSendMessage(force: QueryMode | null = null) {
   if (isListening.value) {
      stopListening();
   }

   if (model.value.trim() === '' || props.isLoading)
      return;
   emit('sendMessage', model.value, force);
   if (props.clearInputOnSend) {
      model.value = '';
   }
}

/**
 * ENG-436: on a routable input, modifier + Enter skips the analysis round and
 * goes straight to the named interface. A bare Enter behaves as it always has.
 */
function handleEnter(event: KeyboardEvent) {
   handleSendMessage(props.routable ? forcedModeFromEvent(event) : null);
}

/* ---------------------------------------------------------------------------------------------- */
</script>

<template>
   <div>
      <div
         class="relative w-full h-12 md:h-16"
         :class="bordered && 'p-[3px] bg-gradient-to-r from-primary to-[#3DB771] rounded-full'"
      >
         <div class="w-full h-full flex items-center bg-white rounded-full p-2">
            <!-- region: microphone button -->
            <button
               class="p-2 rounded-full"
               :class="isListening ? 'bg-primary/10 hover:bg-primary/20' : 'bg-white hover:bg-neutral '"
               :disabled="!isSupported || props.isLoading"
               aria-label="Start or stop speech recognition"
               @click="isListening ? stopListening() : startListening()"
            >
               <Icon
                  :icon="isListening ? IconLib.chat.pause : IconLib.chat.mic"
                  class="size-5 md:size-7"
                  :class="isListening ? 'text-primary' : 'text-black'"
               />
            </button>
            <!-- endregion: microphone button -->

            <input
               ref="inputRef"
               v-model="model"
               class="focus:outline-none flex-1 bg-white placeholder:text-neutral-darker p-2 min-w-0"
               type="text"
               :placeholder="placeholder"
               :aria-label="placeholder"
               @keydown.enter="handleEnter"
               @input="handleInputChange"
            >

            <!-- The routing explanation rides on the control that performs it,
                 rather than as a standing line under the input; the caret beside
                 it offers the same choice where the shortcuts can't be pressed. -->
            <QueryModeSubmit
               :show-menu="routable"
               :disabled="isLoading"
               @force="handleSendMessage"
            >
               <UiTooltip
                  :content="queryModeHint()"
                  :disabled="!routable"
                  placement="top-end"
                  multiline
               >
                  <button
                     class="p-2 bg-white hover:bg-neutral rounded-full"
                     aria-label="Send message"
                     @click="handleSendMessage()"
                  >
                     <Icon
                        v-if="isLoading"
                        :icon="IconLib.loading.spinner"
                        class="size-5 md:size-7 text-black"
                     />
                     <Icon
                        v-else
                        :icon="IconLib.chat.send"
                        class="size-5 md:size-7 text-black/50"
                     />
                  </button>
               </UiTooltip>
            </QueryModeSubmit>
         </div>
      </div>

      <div v-if="isListening" class="ml-2 mt-1 flex items-center space-x-2 text-sm text-primary">
         <div class="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
         <span>Listening... Speak now</span>
      </div>
   </div>
</template>

<style scoped lang="postcss">

</style>
